import { FinnishSSN } from "finnish-ssn";
import personnummer from "personnummer.js";
const phonePatterns = {
    se: /^\+?\d{1,20}$/,
    no: /(^[0-9]{8}$)|(^[0-9]{3} [0-9]{2} [0-9]{3}$)/,
    dk: /^([0-9]{8})$/,
    fi: /^[0-9]{7,12}$/
};
const phoneHomePatterns = {
    ...phonePatterns,
    se: /^(\+?[0-9 -]+)$/,
    no: /(^[0-9]{8}$)|(^[0-9]{3} [0-9]{2} [0-9]{3}$)|(^[0-9]{2} [0-9]{2} [0-9]{2} [0-9]{2}$)/
};
var emailPattern = /\S+@\S+\.\S+/;
export function createValidationHelpers(intl = {}, dateUtils) {
    const { locale = "nb-NO" } = intl;
    const [localeLang, localeCountry = localeLang] = locale.split("-", 2);
    const countryCode = localeCountry.substring(0, 2).toLowerCase();
    function isLengthOfSSN(value) {
        switch (localeLang) {
            case "sv":
                if (!value || value.length !== 10)
                    return false;
                break;
            case "nb":
                if (!value || value.length !== 11)
                    return false;
                break;
        }
        return true;
    }
    function validSsn(value) {
        if (!value)
            return false;
        value = value.replace(/\s/g, "");
        switch (localeLang) {
            case "fi":
                if (value === "Kirjattu")
                    return true;
                break;
            case "en":
                if (value === "Registered")
                    return true;
                break;
            case "sv":
                if (value === "Registrerad")
                    return true;
                break;
            case "dk":
                if (value === "Registreret")
                    return true;
                break;
            case "nb":
            default:
                if (value === "Registered" || value === "Registrert")
                    return true;
        }
        switch (countryCode) {
            case "fi":
                return FinnishSSN.validate(value);
            case "se":
                return value.length < 12 && personnummer.validate(value);
            case "dk":
                return value.length === 10;
            case "no":
            default:
                return isValidNorwegianSSN(value);
        }
    }
    function validKid(value) {
        function isValidKidMod10(num) {
            const s = num.toString();
            const base = s.slice(0, -1);
            const checkDigit = Number(s[s.length - 1]);
            const digits = base.toString().split("").map(Number).reverse();
            let sum = 0;
            for (let i = 0; i < digits.length; i++) {
                let d = digits[i];
                // Double every second digit
                if (i % 2 === 0) {
                    d *= 2;
                    if (d > 9)
                        d -= 9;
                }
                sum += d;
            }
            const expected = (10 - (sum % 10)) % 10;
            return checkDigit === expected;
        }
        function isValidKidMod11(num) {
            const s = num.toString();
            const base = s.slice(0, -1);
            const checkDigit = Number(s[s.length - 1]);
            const digits = base.split("").map(Number).reverse();
            const weights = [2, 3, 4, 5, 6, 7];
            let sum = 0;
            for (let i = 0; i < digits.length; i++) {
                sum += digits[i] * weights[i % weights.length];
            }
            const remainder = sum % 11;
            const expected = remainder === 0
                ? 0
                : remainder === 1
                    ? -1 // invalid KID
                    : 11 - remainder;
            return expected === checkDigit;
        }
        return value != null && (isValidKidMod10(value) || isValidKidMod11(value));
    }
    function isValidNorwegianSSN(ssn) {
        if (!ssn || !ssn.match("^[0-9]{11}$")) {
            return false;
        }
        const calculateCheckSumMod11 = function (ssn, factors) {
            let sum = 0;
            for (let i = 0; i < factors.length; ++i) {
                sum += parseInt(ssn.charAt(i), 10) * factors[i];
            }
            const checkSum = 11 - (sum % 11);
            return checkSum === 11 ? 0 : checkSum;
        };
        return (calculateCheckSumMod11(ssn, [3, 7, 6, 1, 8, 9, 4, 5, 2]) ===
            parseInt(ssn.charAt(9), 10) &&
            calculateCheckSumMod11(ssn, [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]) ===
                parseInt(ssn.charAt(10), 10));
    }
    function validOrgNumber(value) {
        if (value == null)
            return false;
        value = value.replace(/\s/g, "");
        switch (countryCode) {
            case "fi":
                return value.length === 8;
            case "se":
                return personnummer.validateCIN(value);
            case "dk":
                return value.length === 8;
            case "no":
            default:
                return value.length === 9;
        }
    }
    function phoneValidation(value) {
        if (!value)
            return false;
        const pattern = phonePatterns[countryCode] ?? phonePatterns.no;
        return value.match(pattern) != null;
    }
    function phoneHomeValidation(value) {
        if (!value)
            return false;
        const pattern = phoneHomePatterns[countryCode] ?? phoneHomePatterns.no;
        return value.match(pattern) != null;
    }
    function emailValidation(value) {
        if (!value)
            return false;
        return value.match(emailPattern) != null;
    }
    function formatNumber(value, options) {
        if (!value)
            return "0";
        return intl.formatNumber?.(value, options) ?? value;
    }
    function getMonthsBetweenDates(startDate, endDate) {
        // Calculate base difference in months
        let months = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
            (endDate.getMonth() - startDate.getMonth());
        // If there are leftover days (partial month), round up
        if (endDate.getDate() > startDate.getDate()) {
            months += 1;
        }
        // Ensure non-negative result
        return months <= 0 ? 0 : months;
    }
    function getYearsBetweenDates(startDate, endDate) {
        let years = endDate.getFullYear() - startDate.getFullYear();
        // If end month/day is before start month/day, reduce by one
        if (endDate.getMonth() < startDate.getMonth() ||
            (endDate.getMonth() === startDate.getMonth() &&
                endDate.getDate() < startDate.getDate())) {
            years -= 1;
        }
        return years <= 0 ? 0 : years;
    }
    return {
        isLengthOfSSN,
        validOrgNumber,
        validSsn,
        validKid,
        isValidNorwegianSSN,
        phoneValidation,
        phoneHomeValidation,
        emailValidation,
        formatNumber,
        getMonthsBetweenDates,
        getYearsBetweenDates
    };
}
//# sourceMappingURL=validationHelpers.js.map